import type { GcPriorityDef, GcPriorityKey, GcSuggestion } from './types';

export const GC_PRIORITY: Record<GcPriorityKey, GcPriorityDef> = {
  high: { label: 'High priority', tone: 'terra' },
  medium: { label: 'Medium priority', tone: 'gold' },
  low: { label: 'Low priority', tone: 'neutral' },
};

export const GC_SUGGESTIONS: GcSuggestion[] = [
  {
    id: 1,
    name: 'Teeth Whitening — Spring Special',
    confidence: 94,
    priority: 'high',
    estBudget: 250,
    estCPC: 0.85,
    estBookings: '12–18',
    estCostPerPatient: '$14–21',
    keywords: ['teeth whitening near me', 'professional whitening cost', 'teeth whitening deals'],
    adPreview: {
      headline: 'Professional Teeth Whitening — $199 Spring Special',
      desc: 'Get a brighter smile in one visit. ADA-certified, gentle whitening. Book your appointment today — limited availability.',
    },
    marketSignals: [
      { icon: 'pulse', text: '“Teeth whitening” searches up 34% this month (seasonal spring demand)' },
      { icon: 'dollar', text: 'Average CPC only $0.85 — 3 competitors paused their whitening ads' },
      { icon: 'target', text: 'None of the 8 local competitors are running a spring promo right now' },
    ],
    clinicSignals: [
      { icon: 'phone', text: '14 callers in the last 30 days asked about whitening pricing' },
      { icon: 'sparkle', text: 'Your whitening reviews average 4.9 stars — strong social proof asset' },
      { icon: 'check-circ', text: 'Dr. Martinez completed 22 whitening procedures last quarter' },
    ],
    reasoning:
      'High patient demand + zero competitor activity + low CPC creates an ideal window. ELVA estimates this campaign will pay for itself within the first 3 bookings.',
  },
  {
    id: 2,
    name: 'Spanish — Dentista de Emergencia',
    confidence: 91,
    priority: 'high',
    estBudget: 150,
    estCPC: 0.67,
    estBookings: '8–12',
    estCostPerPatient: '$13–19',
    keywords: ['dentista cerca de mi', 'dentista de emergencia', 'dentista que habla español'],
    adPreview: {
      headline: 'Dentista de Emergencia — Citas el Mismo Día',
      desc: 'Atención dental de emergencia. Hablamos español. Aceptamos la mayoría de seguros. Llame ahora para una cita hoy.',
    },
    marketSignals: [
      {
        icon: 'team',
        text: 'Spanish-speaking population is 30% of your zip code but only 1 competitor runs Spanish ads',
      },
      { icon: 'dollar', text: 'CPC for Spanish dental terms is 60% lower than English equivalents' },
      { icon: 'bars', text: '“Dentista cerca de mi” search volume growing 18% quarter over quarter' },
    ],
    clinicSignals: [
      { icon: 'chat', text: 'Your front desk has 2 bilingual staff members (Spanish/English)' },
      {
        icon: 'phone',
        text: '8 Spanish-language calls received last month — currently no dedicated landing page',
      },
      { icon: 'sparkle', text: '3 of your 5-star Google reviews are written in Spanish' },
    ],
    reasoning:
      'Massively underserved local segment that your clinic is already equipped to handle. Near-zero competition means very low CPC and high conversion potential.',
  },
  {
    id: 3,
    name: 'Weekend Implant Consultations',
    confidence: 87,
    priority: 'high',
    estBudget: 500,
    estCPC: 3.2,
    estBookings: '4–6',
    estCostPerPatient: '$83–125',
    keywords: [
      'dental implants consultation',
      'same day implant consult',
      'all-on-4 near me',
      'implant cost',
    ],
    adPreview: {
      headline: 'Free Implant Consultation — Saturday Appointments Available',
      desc: 'Considering dental implants? Free consultation with 3D scan included. Flexible financing from $89/mo. Weekend appointments available.',
    },
    marketSignals: [
      { icon: 'calendar', text: 'No competitor runs implant ads on Saturday/Sunday — wide open window' },
      { icon: 'pulse', text: 'Weekend searches for “dental implants” are 40% of total weekly volume' },
      { icon: 'dollar', text: 'Weekend CPC is $3.20 vs $5.80 weekday average — 45% cheaper' },
    ],
    clinicSignals: [
      { icon: 'check-circ', text: 'Dr. Chen performs All-on-4 and is available Saturdays' },
      { icon: 'dollar', text: 'Average implant case value: $4,200 — high ROI even at higher CPC' },
      {
        icon: 'phone',
        text: '6 implant inquiry calls came in on weekends last month with no ads running',
      },
    ],
    reasoning:
      'High-value procedure with weekend demand that competitors ignore. Even at $125/patient acquisition cost, a single implant case ($4,200) delivers 33x ROI.',
  },
  {
    id: 4,
    name: 'Pediatric First Visit — Back to School',
    confidence: 82,
    priority: 'medium',
    estBudget: 200,
    estCPC: 0.92,
    estBookings: '10–15',
    estCostPerPatient: '$13–20',
    keywords: [
      'kids dentist near me',
      'pediatric dentist',
      'child first dental visit',
      'back to school dental checkup',
    ],
    adPreview: {
      headline: "Kids' Dental Checkup — Fun & Gentle First Visits",
      desc: "Back-to-school dental exams. Kid-friendly office with TV and games. Most insurance accepted. Book your child's visit today!",
    },
    marketSignals: [
      {
        icon: 'pulse',
        text: '“Back to school dentist” searches begin ramping in May — early mover advantage',
      },
      { icon: 'target', text: 'Only 2 of 8 competitors have pediatric-specific landing pages' },
      { icon: 'dollar', text: 'Pediatric dental CPC averages $0.92 — low cost, high volume' },
    ],
    clinicSignals: [
      { icon: 'team', text: '28% of your current patients have children under 12 on file' },
      { icon: 'home', text: "Your office has a dedicated kids' play area — use in ad imagery" },
      {
        icon: 'sparkle',
        text: 'Multiple reviews mention “great with kids” and “my daughter loves coming here”',
      },
    ],
    reasoning:
      'Seasonal opportunity with early timing advantage. Families are high-LTV patients — one child often brings siblings and parents. Low competition makes this cost-effective.',
  },
  {
    id: 5,
    name: 'Invisalign — Competitor Gap',
    confidence: 78,
    priority: 'medium',
    estBudget: 400,
    estCPC: 2.1,
    estBookings: '6–9',
    estCostPerPatient: '$44–67',
    keywords: ['invisalign near me', 'invisalign cost', 'clear aligners', 'invisalign vs braces'],
    adPreview: {
      headline: 'Invisalign from $149/mo — Free Consultation',
      desc: 'Straighten your teeth invisibly. Diamond-level Invisalign provider. Flexible payment plans. See your smile preview before you start.',
    },
    marketSignals: [
      {
        icon: 'bars',
        text: 'Bright Smiles Co. paused their Invisalign campaign 2 weeks ago — CPC dropped 22%',
      },
      { icon: 'pulse', text: '“Invisalign cost” searches up 15% as summer approaches' },
      {
        icon: 'alert',
        text: 'Smile Central still dominates Map Pack — this campaign targets search ads only',
      },
    ],
    clinicSignals: [
      {
        icon: 'check-circ',
        text: "You're a Diamond-level Invisalign provider — strong trust signal for ad copy",
      },
      { icon: 'phone', text: '9 Invisalign inquiry calls in the last 30 days' },
      { icon: 'dollar', text: 'Average Invisalign case value: $5,500 — excellent ROI potential' },
    ],
    reasoning:
      'Competitor retreat created a CPC window. Your Diamond provider status is a strong differentiator. Medium confidence because Smile Central still dominates organically.',
  },
  {
    id: 6,
    name: 'Dental Anxiety / Sedation',
    confidence: 71,
    priority: 'low',
    estBudget: 150,
    estCPC: 1.45,
    estBookings: '3–5',
    estCostPerPatient: '$30–50',
    keywords: [
      'sedation dentistry',
      'dentist for anxious patients',
      'sleep dentistry near me',
      'pain-free dentist',
    ],
    adPreview: {
      headline: 'Afraid of the Dentist? We Understand — Sedation Available',
      desc: 'Gentle, judgment-free dental care. IV and oral sedation options. Over 200 five-star reviews from anxious patients like you.',
    },
    marketSignals: [
      { icon: 'bars', text: '“Dental anxiety” searches growing 12% YoY — underserved niche' },
      {
        icon: 'target',
        text: 'Zero competitors in your radius are running sedation-specific campaigns',
      },
      {
        icon: 'dollar',
        text: 'Low search volume but very high conversion intent — estimated 8% CTR',
      },
    ],
    clinicSignals: [
      {
        icon: 'sparkle',
        text: 'ELVA detected 18 reviews mentioning “anxiety,” “nervous,” or “scared” — all positive outcomes',
      },
      { icon: 'check-circ', text: 'Dr. Chen is certified for IV sedation' },
      {
        icon: 'phone',
        text: 'Call analysis: 4 callers last month specifically asked about sedation options',
      },
    ],
    reasoning:
      'Niche segment with zero competition. Lower volume but very high intent — patients searching for sedation dentistry are actively trying to overcome a barrier to treatment. Lower confidence due to smaller addressable market.',
  },
];
